import { useEffect, useMemo, useState } from 'react';
import { X, ExternalLink, Users, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { apiService } from '@/services/api';
import type { DivisionPlayer, PlayerGameStats } from '@/types/api';

interface TeamDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  teamName: string;
  divisionLabel: string;
  players: DivisionPlayer[];
}

const GAME_LABELS: Record<string, string> = {
  '8ball': '8-Ball',
  '9ball': '9-Ball',
  '10ball': '10-Ball',
};

const GAME_ORDER = ['8ball', '9ball', '10ball'];

type SortKey =
  | 'player'
  | 'game'
  | 'csr'
  | 'class'
  | 'mp'
  | 'wins'
  | 'losses'
  | 'winPct'
  | 'pointsFor'
  | 'pointsAgainst'
  | 'local';

interface PlayerGameRow {
  playerId: string;
  playerName: string;
  gameType: string;
  stats: PlayerGameStats;
}

const CLASS_ORDER: Record<string, number> = {
  Master: 0, A: 1, B: 2, C: 3, D: 4, E: 5,
};

function fmtNum(n: number | undefined | null, digits = 0): string {
  if (n == null) return '—';
  return n.toFixed(digits);
}

function hasAnyStats(s: PlayerGameStats | undefined): boolean {
  if (!s) return false;
  return (
    s.csr != null ||
    s.skill_level != null ||
    (s.matches_played ?? 0) > 0 ||
    (s.points_for ?? 0) > 0 ||
    (s.points_against ?? 0) > 0
  );
}

function normalize(name: string): string {
  return name.trim().toUpperCase().replace(/\s+/g, ' ');
}

function sortValue(row: PlayerGameRow, key: SortKey): number | string {
  const s = row.stats;
  switch (key) {
    case 'player': return row.playerName.toLowerCase();
    case 'game': return GAME_ORDER.indexOf(row.gameType);
    case 'csr': return s.csr ?? -Infinity;
    case 'class': return s.skill_class ? (CLASS_ORDER[s.skill_class] ?? 99) : 99;
    case 'mp': return s.matches_played ?? -1;
    case 'wins': return s.wins ?? -1;
    case 'losses': return s.losses ?? -1;
    case 'winPct': return s.win_percentage ?? -1;
    case 'pointsFor': return s.points_for ?? -1;
    case 'pointsAgainst': return s.points_against ?? -1;
    case 'local': {
      const r = s.local_rank;
      if (!r) return Number.POSITIVE_INFINITY;
      const n = parseInt(r, 10);
      return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
    }
  }
}

function compareRows(a: PlayerGameRow, b: PlayerGameRow, key: SortKey, dir: 'asc' | 'desc'): number {
  const av = sortValue(a, key);
  const bv = sortValue(b, key);
  let c: number;
  if (typeof av === 'number' && typeof bv === 'number') {
    c = av - bv;
  } else {
    c = String(av).localeCompare(String(bv));
  }
  if (c === 0) {
    // Stable secondary sort: player name then game order
    c = a.playerName.localeCompare(b.playerName);
    if (c === 0) c = GAME_ORDER.indexOf(a.gameType) - GAME_ORDER.indexOf(b.gameType);
  }
  return dir === 'asc' ? c : -c;
}

export default function TeamDetailModal({
  isOpen,
  onClose,
  teamName,
  divisionLabel,
  players,
}: TeamDetailModalProps) {
  const [gameFilter, setGameFilter] = useState<'all' | '8ball' | '9ball' | '10ball'>('all');
  const [sortKey, setSortKey] = useState<SortKey>('player');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  // Reset filter/sort whenever the modal target changes
  useEffect(() => {
    if (isOpen) {
      setGameFilter('all');
      setSortKey('player');
      setSortDir('asc');
    }
  }, [isOpen, teamName, divisionLabel]);

  const teamPlayers = useMemo(() => {
    const target = normalize(teamName);
    return players.filter(p => p.team_name && normalize(p.team_name) === target);
  }, [players, teamName]);

  // Which game types are actually present on this team's roster?
  const availableGames = useMemo(() => {
    const set = new Set<string>();
    for (const p of teamPlayers) {
      for (const gt of GAME_ORDER) if (hasAnyStats(p.games?.[gt])) set.add(gt);
    }
    return GAME_ORDER.filter(gt => set.has(gt));
  }, [teamPlayers]);

  const rows = useMemo<PlayerGameRow[]>(() => {
    const out: PlayerGameRow[] = [];
    for (const p of teamPlayers) {
      const gts = GAME_ORDER.filter(gt => hasAnyStats(p.games?.[gt]));
      for (const gt of gts) {
        if (gameFilter !== 'all' && gameFilter !== gt) continue;
        out.push({
          playerId: p.player_id,
          playerName: p.name,
          gameType: gt,
          stats: p.games[gt] as PlayerGameStats,
        });
      }
    }
    out.sort((a, b) => compareRows(a, b, sortKey, sortDir));
    return out;
  }, [teamPlayers, gameFilter, sortKey, sortDir]);

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      // Sensible default direction per column type
      const numericDescByDefault: SortKey[] = [
        'csr', 'mp', 'wins', 'losses', 'winPct', 'pointsFor', 'pointsAgainst',
      ];
      setSortDir(numericDescByDefault.includes(key) ? 'desc' : 'asc');
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 modal-backdrop flex items-end md:items-center justify-center p-0 md:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full h-full md:h-auto md:rounded-2xl shadow-2xl md:max-w-5xl md:max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-white px-4 md:px-6 py-4 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
          <div className="min-w-0">
            <h2 className="text-base md:text-lg font-bold text-slate-900 truncate">{teamName}</h2>
            <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
              <span className="font-medium uppercase tracking-wide">{divisionLabel}</span>
              <span className="text-slate-300">•</span>
              <span className="inline-flex items-center gap-1">
                <Users className="w-3 h-3" />
                {teamPlayers.length} player{teamPlayers.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 active:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Game filter */}
        {availableGames.length > 1 && (
          <div className="px-4 md:px-6 py-2 border-b border-slate-100 flex items-center gap-1.5 overflow-x-auto flex-shrink-0">
            <FilterPill active={gameFilter === 'all'} onClick={() => setGameFilter('all')}>
              All games
            </FilterPill>
            {availableGames.map((gt) => (
              <FilterPill
                key={gt}
                active={gameFilter === gt}
                onClick={() => setGameFilter(gt as typeof gameFilter)}
              >
                {GAME_LABELS[gt]}
              </FilterPill>
            ))}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {rows.length === 0 ? (
            <div className="py-16 text-center text-sm text-slate-500">
              No player data available for this filter.
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-slate-50 border-b border-slate-200 z-10">
                    <tr className="text-xs text-slate-500">
                      <SortHeader label="Player" col="player" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="left" />
                      <SortHeader label="Game" col="game" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="left" />
                      <SortHeader label="CSR" col="csr" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right" />
                      <SortHeader label="Class" col="class" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="center" />
                      <SortHeader label="MP" col="mp" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right" />
                      <SortHeader label="W" col="wins" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right" />
                      <SortHeader label="L" col="losses" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right" />
                      <SortHeader label="Win %" col="winPct" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right" />
                      <SortHeader label="Pts For" col="pointsFor" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right" />
                      <SortHeader label="Pts Allowed" col="pointsAgainst" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right" />
                      <SortHeader label="Local" col="local" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right" />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, idx) => {
                      const s = row.stats;
                      const prev = rows[idx - 1];
                      const showPlayer =
                        sortKey === 'player' ? !prev || prev.playerId !== row.playerId : true;
                      return (
                        <tr key={`${row.playerId}-${row.gameType}`} className="border-b border-slate-50 hover:bg-slate-50/60">
                          <td className="py-2 px-4">
                            {showPlayer ? <PlayerCell playerId={row.playerId} playerName={row.playerName} /> : null}
                          </td>
                          <td className="py-2 px-3 text-slate-600">{GAME_LABELS[row.gameType]}</td>
                          <td className="py-2 px-3 text-right tabular-nums font-medium text-slate-700">
                            {fmtNum(s.csr, 0)}
                          </td>
                          <td className="py-2 px-3 text-center text-slate-500">
                            {s.skill_class ?? '—'}
                          </td>
                          <td className="py-2 px-3 text-right tabular-nums text-slate-500">
                            {s.matches_played ?? 0}
                          </td>
                          <td className="py-2 px-3 text-right tabular-nums font-semibold text-emerald-600">
                            {s.wins ?? 0}
                          </td>
                          <td className="py-2 px-3 text-right tabular-nums text-slate-500">
                            {s.losses ?? 0}
                          </td>
                          <td className="py-2 px-3 text-right tabular-nums text-slate-600">
                            {s.win_percentage != null ? `${s.win_percentage.toFixed(1)}%` : '—'}
                          </td>
                          <td className="py-2 px-3 text-right tabular-nums text-emerald-700">
                            {s.points_for ?? '—'}
                          </td>
                          <td className="py-2 px-3 text-right tabular-nums text-slate-400">
                            {s.points_against ?? '—'}
                          </td>
                          <td className="py-2 px-4 text-right tabular-nums text-slate-500">
                            {s.local_rank ?? '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden divide-y divide-slate-100">
                {/* On mobile, group by player when sorted by player; otherwise show flat list */}
                {sortKey === 'player' ? (
                  groupedByPlayer(rows).map(({ playerId, playerName, items }) => (
                    <div key={playerId} className="px-4 py-3">
                      <div className="flex items-baseline justify-between gap-3 mb-2">
                        <PlayerCell playerId={playerId} playerName={playerName} />
                      </div>
                      <div className="space-y-1.5">
                        {items.map((row) => (
                          <MobileGameRow key={row.gameType} row={row} />
                        ))}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="divide-y divide-slate-100">
                    {rows.map((row) => (
                      <div key={`${row.playerId}-${row.gameType}`} className="px-4 py-3">
                        <PlayerCell playerId={row.playerId} playerName={row.playerName} />
                        <div className="mt-1.5">
                          <MobileGameRow row={row} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function FilterPill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`shrink-0 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
        active
          ? 'bg-brand-600 text-white'
          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
      }`}
    >
      {children}
    </button>
  );
}

function SortHeader({
  label,
  col,
  sortKey,
  sortDir,
  onSort,
  align,
}: {
  label: string;
  col: SortKey;
  sortKey: SortKey;
  sortDir: 'asc' | 'desc';
  onSort: (k: SortKey) => void;
  align: 'left' | 'right' | 'center';
}) {
  const isActive = sortKey === col;
  const Icon = !isActive ? ArrowUpDown : sortDir === 'asc' ? ArrowUp : ArrowDown;
  const justify =
    align === 'left' ? 'justify-start' : align === 'right' ? 'justify-end' : 'justify-center';
  const padding = align === 'left' ? 'px-4' : 'px-3';
  return (
    <th className={`py-2 ${padding} font-medium text-${align}`}>
      <button
        type="button"
        onClick={() => onSort(col)}
        className={`inline-flex items-center gap-1 ${justify} hover:text-slate-800 transition-colors ${
          isActive ? 'text-slate-700 font-semibold' : ''
        }`}
      >
        <span>{label}</span>
        <Icon className={`w-3 h-3 ${isActive ? 'opacity-100' : 'opacity-40'}`} />
      </button>
    </th>
  );
}

function PlayerCell({ playerId, playerName }: { playerId: string; playerName: string }) {
  return (
    <div>
      <div className="font-medium text-slate-900">{playerName}</div>
      <a
        href={apiService.getPlayerProfileUrl(playerId)}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-brand-600 transition-colors"
      >
        #{playerId}
        <ExternalLink className="w-3 h-3" />
      </a>
    </div>
  );
}

function MobileGameRow({ row }: { row: PlayerGameRow }) {
  const s = row.stats;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs bg-slate-50/70 rounded-md px-2.5 py-1.5">
      <span className="font-medium text-slate-700 w-14 shrink-0">{GAME_LABELS[row.gameType]}</span>
      <span className="tabular-nums text-slate-600">
        CSR <span className="font-semibold">{fmtNum(s.csr, 0)}</span>
        {s.skill_class ? <span className="text-slate-400 ml-1">{s.skill_class}</span> : null}
      </span>
      <span className="tabular-nums">
        <span className="font-semibold text-emerald-600">{s.wins ?? 0}</span>
        <span className="text-slate-300">-</span>
        <span className="text-slate-500">{s.losses ?? 0}</span>
      </span>
      <span className="tabular-nums text-slate-500">
        Pts <span className="text-emerald-700 font-semibold">{s.points_for ?? '—'}</span>
        <span className="text-slate-300"> / </span>
        <span>{s.points_against ?? '—'}</span>
      </span>
    </div>
  );
}

function groupedByPlayer(rows: PlayerGameRow[]): {
  playerId: string;
  playerName: string;
  items: PlayerGameRow[];
}[] {
  const out: { playerId: string; playerName: string; items: PlayerGameRow[] }[] = [];
  for (const r of rows) {
    const last = out[out.length - 1];
    if (last && last.playerId === r.playerId) {
      last.items.push(r);
    } else {
      out.push({ playerId: r.playerId, playerName: r.playerName, items: [r] });
    }
  }
  return out;
}
